import {
  CONFIG_STATUS_LEVEL,
  CONFIG_TYPES,
  CRM_FILTER_TEMPLATE,
  ENTITY_LEVEL,
  SIDE_NAV_BAR_OPTIONS,
} from '../../../../common/constants/integrationConstants/crmConstants';
import { findInSyncFilterStatus } from '../../../../common/util/crmIntegrationUtils/crmUtils';
import { deepCloneTemplate } from '../../../../common/util/reducerUtils/CrmReducerUtils';
import {
  addInSyncFilter,
  cancelInSyncFilter,
  changeCrmInSyncFilter,
  clearUnsavedChanges,
  createInSyncFilter,
  fetchCrmInSyncFilters,
  removeInSyncFilterRow,
} from '../../../routines/integrations/crmRoutines';

const initialState = {
  [CONFIG_TYPES.IN_SYNC]: {
    organization: deepCloneTemplate(CRM_FILTER_TEMPLATE),
    account: deepCloneTemplate(CRM_FILTER_TEMPLATE),
  },
  persistedStateFilters: null,
  filterLoading: false,
  error: null,
  hasUnSavedConfig: false,
};

const crmFilterReducer = (state = initialState, action) => {
  switch (action.type) {
    case addInSyncFilter.TRIGGER: {
      const { entityLevel, filterId } = action.payload;
      const newInSyncState = {
        ...state[CONFIG_TYPES.IN_SYNC],
        [entityLevel]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
          filters: {
            ...state[CONFIG_TYPES.IN_SYNC][entityLevel].filters,
            [filterId]: {
              filterId,
              status: CONFIG_STATUS_LEVEL.IN_PROGRESS,
            },
          },
          status: CONFIG_STATUS_LEVEL.IN_PROGRESS,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncState,
        hasUnSavedConfig: true,
      };
    }
    case createInSyncFilter.TRIGGER:
      return {
        ...state,
        error: null,
      };
    case createInSyncFilter.SUCCESS: {
      const { entityLevel, filters } = action.payload;
      const newInSyncState = {
        ...state[CONFIG_TYPES.IN_SYNC],
        [entityLevel]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
          filters: Object.values(filters).map((filter, index) => ({
            ...filter,
            filterId: index,
          })),
          status: CONFIG_STATUS_LEVEL.SAVED,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncState,
        persistedStateFilters: newInSyncState,
        filterLoading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case createInSyncFilter.FAILURE:
      return {
        ...state,
        filterLoading: false,
        error: action.payload.error,
        hasUnSavedConfig: false,
      };

    case fetchCrmInSyncFilters.TRIGGER:
      return {
        ...state,
        filterLoading: true,
        error: false,
      };
    case fetchCrmInSyncFilters.SUCCESS: {
      const { filters } = action.payload;
      let newInSyncState;
      if (!filters) {
        newInSyncState = {
          ...state[CONFIG_TYPES.IN_SYNC],
          organization: {
            ...state[CONFIG_TYPES.IN_SYNC].organization,
            filters: {},
          },
          account: {
            ...state[CONFIG_TYPES.IN_SYNC].account,
            filters: {},
          },
        };
      } else {
        newInSyncState = {
          ...state[CONFIG_TYPES.IN_SYNC],
          organization: {
            ...state[CONFIG_TYPES.IN_SYNC].organization,
            filters: filters?.organization.reduce(
              (previous, current) => ({
                ...previous,
                [current.filterId]: current,
              }),
              {}
            ),
          },
          account: {
            ...state[CONFIG_TYPES.IN_SYNC].account,
            filters: filters?.account.reduce(
              (previous, current) => ({
                ...previous,
                [current.filterId]: current,
              }),
              {}
            ),
          },
        };
        if (filters.organization.length > 0) {
          newInSyncState.organization.status = CONFIG_STATUS_LEVEL.SAVED;
        }
        if (filters.account.length > 0) {
          newInSyncState.account.status = CONFIG_STATUS_LEVEL.SAVED;
        }
      }
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncState,
        persistedStateFilters: newInSyncState,
        filterLoading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case removeInSyncFilterRow.TRIGGER: {
      const { entityLevel, filterId } = action.payload;
      const newInSyncState = {
        ...state[CONFIG_TYPES.IN_SYNC],
        [entityLevel]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
          filters: {
            ...state[CONFIG_TYPES.IN_SYNC][entityLevel].filters,
          },
        },
      };
      delete newInSyncState[entityLevel].filters[filterId];
      newInSyncState[entityLevel].filters = Object.values(
        newInSyncState[entityLevel].filters
      ).reduce((map, filter, index) => {
        map[index] = { ...filter, filterId: index };
        return map;
      }, {});

      const newInSyncStateStatus = findInSyncFilterStatus(
        newInSyncState[entityLevel].filters
      );
      newInSyncState[entityLevel].status = newInSyncStateStatus;
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncState,
        hasUnSavedConfig: true,
      };
    }
    case changeCrmInSyncFilter.TRIGGER: {
      const { entityLevel, filterId, columnId, value } = action.payload;
      const newInSyncState = {
        ...state[CONFIG_TYPES.IN_SYNC],
        [entityLevel]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
          filters: {
            ...state[CONFIG_TYPES.IN_SYNC][entityLevel].filters,
            [filterId]: {
              ...state[CONFIG_TYPES.IN_SYNC][entityLevel].filters[filterId],
              [columnId]: value,
            },
          },
        },
      };
      const newInSyncStateStatus = findInSyncFilterStatus(
        newInSyncState[entityLevel].filters
      );
      newInSyncState[entityLevel].status = newInSyncStateStatus;

      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncState,
        hasUnSavedConfig: true,
      };
    }
    case cancelInSyncFilter.TRIGGER: {
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: JSON.parse(
          JSON.stringify(state.persistedStateFilters)
        ),
        hasUnSavedConfig: false,
      };
    }
    case clearUnsavedChanges.TRIGGER: {
      const { entityTab, sideNavBarTab } = action.payload;

      const { hasUnSavedConfig } = { ...state };
      if (
        sideNavBarTab === SIDE_NAV_BAR_OPTIONS.IN_SYNC &&
        entityTab !== ENTITY_LEVEL.CONTACT &&
        hasUnSavedConfig
      ) {
        return {
          ...state,
          [CONFIG_TYPES.IN_SYNC]: JSON.parse(
            JSON.stringify(state.persistedStateFilters)
          ),
          hasUnSavedConfig: false,
        };
      }
      return {
        ...state,
      };
    }

    default:
      return state;
  }
};
export default crmFilterReducer;
